import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Grid,
  Card,
  CardMedia,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import UploadIcon from '@mui/icons-material/Upload';
import { api } from '../../context/AuthContext';

// Import all images from assets folder
// You need to import each image you want to use
import qrBasic from '../../assets/images/qr-basic.jpg';
import qrFamily from '../../assets/images/qr-family.jpg';
// Import other product images as needed

// Create an image map for easy lookup
const productImages = {
  'qr-basic.jpg': qrBasic,
  'qr-family.jpg': qrFamily,
  // Add other image mappings as needed
};

// Function to resolve image paths
const resolveImagePath = (imagePath) => {
  if (!imagePath) return '';
  
  // If it's already a full URL
  if (imagePath.startsWith('http')) return imagePath;
  
  // Extract the filename from the path
  const filename = imagePath.split('/').pop();
  
  // If we have this image in our map, return the imported version
  if (productImages[filename]) {
    return productImages[filename];
  }
  
  // Fallback to the API URL path for server-side images
  return `${process.env.REACT_APP_API_URL || ''}${imagePath}`;
};

// Styled components
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
  },
}));

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const ProductImagePreview = styled('img')({
  width: '100%',
  maxHeight: '200px',
  objectFit: 'contain',
  marginTop: '16px',
  marginBottom: '8px',
  border: '1px solid #eee',
  borderRadius: '4px',
  padding: '8px',
});

const FeatureChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
}));

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: '',
    price: '',
    features: [],
    popular: false,
    active: true,
  });
  const [featuresString, setFeaturesString] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  // Debug for image paths
  useEffect(() => {
    if (products.length > 0) {
      console.log('Product image debug info:');
      console.log('Original imageUrl:', products[0].imageUrl);
      console.log('Resolved URL:', resolveImagePath(products[0].imageUrl));
    }
  }, [products]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/products/all');
      setProducts(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(
        err.response?.data?.message || 'Failed to load products. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (product = null) => {
    if (product) {
      setIsEditing(true);
      setFormData({
        id: product.id,
        name: product.name,
        description: product.description,
        price: product.price.toString(),
        features: product.features,
        popular: product.popular,
        active: product.active !== false, // Default to true if undefined
      });
      setFeaturesString(product.features.join('\n'));
      setImagePreview(product.imageUrl ? resolveImagePath(product.imageUrl) : '');
      setCurrentProduct(product);
    } else {
      setIsEditing(false);
      setFormData({
        id: '',
        name: '',
        description: '',
        price: '',
        features: [],
        popular: false,
        active: true,
      });
      setFeaturesString('');
      setImagePreview('');
      setSelectedImage(null);
      setCurrentProduct(null);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setIsEditing(false);
  };

  const handleOpenDeleteDialog = (product) => {
    setCurrentProduct(product);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleOpenPreviewDialog = (product) => {
    setCurrentProduct(product);
    setPreviewDialogOpen(true);
  };

  const handleClosePreviewDialog = () => {
    setPreviewDialogOpen(false);
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleFeaturesChange = (e) => {
    setFeaturesString(e.target.value);
    setFormData({
      ...formData,
      features: e.target.value.split('\n').filter(item => item.trim() !== ''),
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      
      // Basic validation
      if (!formData.id || !formData.name || !formData.price) {
        setNotification({
          open: true,
          message: 'Please fill in all required fields (ID, Name, Price)',
          severity: 'error',
        });
        setLoading(false);
        return;
      }

      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'features') {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });

      if (selectedImage) {
        formDataToSend.append('image', selectedImage);
      }

      if (isEditing) {
        await api.put(`/api/products/${formData.id}`, formDataToSend);
        setNotification({
          open: true,
          message: 'Product updated successfully',
          severity: 'success',
        });
      } else {
        await api.post('/api/products', formDataToSend);
        setNotification({
          open: true,
          message: 'Product created successfully',
          severity: 'success',
        });
      }

      handleCloseDialog();
      fetchProducts();
    } catch (err) {
      console.error('Error saving product:', err);
      setNotification({
        open: true,
        message: err.response?.data?.message || 'Error saving product',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async () => {
    try {
      setLoading(true);
      await api.delete(`/api/products/${currentProduct.id}`);
      setNotification({
        open: true,
        message: 'Product deactivated successfully',
        severity: 'success',
      });
      handleCloseDeleteDialog();
      fetchProducts();
    } catch (err) {
      console.error('Error deactivating product:', err);
      setNotification({
        open: true,
        message: err.response?.data?.message || 'Error deactivating product',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePermanentDeleteProduct = async () => {
    try {
      setLoading(true);
      await api.delete(`/api/products/${currentProduct.id}/permanent`);
      setNotification({
        open: true,
        message: 'Product permanently deleted',
        severity: 'success',
      });
      handleCloseDeleteDialog();
      fetchProducts();
    } catch (err) {
      console.error('Error deleting product:', err);
      setNotification({
        open: true,
        message: err.response?.data?.message || 'Error deleting product',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false,
    });
  };

  const handleSeedProducts = async () => {
    try {
      setLoading(true);
      await api.post('/api/products/seed');
      setNotification({
        open: true,
        message: 'Initial products seeded successfully',
        severity: 'success',
      });
      fetchProducts();
    } catch (err) {
      console.error('Error seeding products:', err);
      setNotification({
        open: true,
        message: err.response?.data?.message || 'Error seeding products',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading && products.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" component="h1">
          Product Management
        </Typography>
        <Box>
          {products.length === 0 && (
            <Button
              variant="outlined"
              color="primary"
              onClick={handleSeedProducts}
              sx={{ mr: 2 }}
            >
              Seed Initial Products
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add New Product
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {products.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            No products found
          </Typography>
          <Typography variant="body1" color="textSecondary" paragraph>
            Start by adding a new product or seed initial products
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Popular</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.map((product) => (
                <StyledTableRow key={product._id}>
                  <TableCell>{product.id}</TableCell>
                  <TableCell>{product.name}</TableCell>
                  <TableCell>£{product.price.toFixed(2)}</TableCell>
                  <TableCell>
                    <Chip
                      label={product.active !== false ? 'Active' : 'Inactive'}
                      color={product.active !== false ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {product.popular ? (
                      <Chip label="Popular" color="primary" size="small" />
                    ) : (
                      <Chip label="No" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      color="info"
                      size="small"
                      onClick={() => handleOpenPreviewDialog(product)}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => handleOpenDialog(product)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => handleOpenDeleteDialog(product)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Product Form Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{isEditing ? 'Edit Product' : 'Add New Product'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Product ID"
                name="id"
                value={formData.id}
                onChange={handleChange}
                fullWidth
                margin="normal"
                required
                disabled={isEditing}
                helperText={isEditing ? "ID cannot be changed" : "Unique identifier for the product"}
              />
              <TextField
                label="Product Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                fullWidth
                margin="normal"
                required
              />
              <TextField
                label="Price (£)"
                name="price"
                value={formData.price}
                onChange={handleChange}
                fullWidth
                margin="normal"
                required
                type="number"
                inputProps={{ min: 0, step: "0.01" }}
              />
              <TextField
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                fullWidth
                margin="normal"
                multiline
                rows={3}
              />
              <Box sx={{ my: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Features (one per line)
                </Typography>
                <TextField
                  name="features"
                  value={featuresString}
                  onChange={handleFeaturesChange}
                  fullWidth
                  multiline
                  rows={5}
                  placeholder="Enter features, one per line"
                  helperText="Each line will be shown as a separate feature"
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Product Image
                </Typography>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  sx={{ mb: 2 }}
                >
                  Select Image
                  <VisuallyHiddenInput
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </Button>
                {imagePreview && (
                  <ProductImagePreview src={imagePreview} alt="Product preview" />
                )}
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.popular}
                      onChange={handleChange}
                      name="popular"
                      color="primary"
                    />
                  }
                  label="Mark as Popular Product"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.active}
                      onChange={handleChange}
                      name="active"
                      color="primary"
                    />
                  }
                  label="Product Active"
                />
              </Box>

              {formData.features.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Preview Features:
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    {formData.features.map((feature, index) => (
                      <FeatureChip key={index} label={feature} />
                    ))}
                  </Paper>
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : isEditing ? 'Update' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
      >
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to deactivate the product "{currentProduct?.name}"?
          </Typography>
          <Typography variant="body2" color="textSecondary">
            This will hide the product from customers but keep it in the database.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button
            onClick={handleDeleteProduct}
            color="primary"
            disabled={loading}
          >
            Deactivate
          </Button>
          <Button
            onClick={handlePermanentDeleteProduct}
            color="error"
            disabled={loading}
          >
            Permanently Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Product Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={handleClosePreviewDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Product Preview</DialogTitle>
        <DialogContent dividers>
          {currentProduct && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                {currentProduct.imageUrl ? (
                  <Card>
                    <CardMedia
                      component="img"
                      height="300"
                      image={resolveImagePath(currentProduct.imageUrl)}
                      alt={currentProduct.name}
                      sx={{ objectFit: 'contain', p: 2 }}
                    />
                  </Card>
                ) : (
                  <Box
                    sx={{
                      height: 300,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.100',
                      border: '1px solid',
                      borderColor: 'grey.300',
                      borderRadius: 1,
                    }}
                  >
                    <Typography color="textSecondary">No image available</Typography>
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h5" gutterBottom>
                  {currentProduct.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  {currentProduct.popular && (
                    <Chip label="Popular" color="primary" size="small" />
                  )}
                  <Chip 
                    label={currentProduct.active !== false ? 'Active' : 'Inactive'} 
                    color={currentProduct.active !== false ? 'success' : 'error'} 
                    size="small" 
                  />
                </Box>
                <Typography variant="h6" color="primary" gutterBottom>
                  £{currentProduct.price.toFixed(2)}
                </Typography>
                <Typography variant="body1" paragraph>
                  {currentProduct.description}
                </Typography>
                <Typography variant="subtitle1" gutterBottom>
                  Features:
                </Typography>
                <Box component="ul" sx={{ pl: 2 }}>
                  {currentProduct.features.map((feature, index) => (
                    <Box component="li" key={index} sx={{ mb: 1 }}>
                      <Typography variant="body2">{feature}</Typography>
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreviewDialog}>Close</Button>
          <Button 
            onClick={() => {
              handleClosePreviewDialog();
              handleOpenDialog(currentProduct);
            }}
            color="primary"
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ProductList;